import { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, Collapse, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material'
import { perguntar, type Mensagem, type RespostaChat } from '../api/chat'
import { useFiltros } from '../state/FiltrosContext'

type Item = { id: number; pergunta: string; resposta?: RespostaChat; erro?: string }

const GUARDA = 'marketinghub:chat'
/** A conversa fica só nesta aba do navegador (some ao fechar): conveniência, não histórico. */
function lerGuardado(): Item[] {
  try {
    const v = JSON.parse(sessionStorage.getItem(GUARDA) ?? '[]')
    return Array.isArray(v) ? v : []
  } catch {
    return []
  }
}

/**
 * A conversa com a IA: perguntas livres sobre os números. A IA escolhe o que buscar, o
 * sistema busca pelo mesmo motor dos painéis e a IA responde só com isso — cada número
 * conferido no servidor. Usada na página Pergunte à IA e na gaveta do botão flutuante (a
 * mesma conversa, guardada na aba).
 */
export function ChatIA({ compacto = false, periodoTela, onNovaConversa }: {
  /** Na gaveta: exemplos e mensagens mais estreitos. */
  compacto?: boolean
  /** Período do painel aberto atrás da gaveta; vale quando a pergunta não diz outro. */
  periodoTela?: { inicio: string; fim: string }
  /** Recebe a função que limpa a conversa (para o botão "Nova conversa" do cabeçalho de quem usa). */
  onNovaConversa?: (limpar: (() => void) | null) => void
}) {
  const { marcaId, marcas } = useFiltros()
  const [itens, setItens] = useState<Item[]>(lerGuardado)
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const fim = useRef<HTMLDivElement>(null)
  const marca = marcas.find((m) => m.id === marcaId)?.nome

  useEffect(() => {
    try {
      sessionStorage.setItem(GUARDA, JSON.stringify(itens.slice(-20)))
    } catch {
      // aba sem armazenamento: a conversa só não sobrevive ao recarregar
    }
    fim.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [itens])

  useEffect(() => {
    onNovaConversa?.(itens.length && !enviando ? () => setItens([]) : null)
  }, [itens.length, enviando, onNovaConversa])

  const enviar = async (pergunta: string) => {
    const p = pergunta.trim()
    if (!p || enviando) return
    const historico: Mensagem[] = itens.flatMap((i) =>
      i.resposta && i.resposta.tipo !== 'fontes' ? [{ papel: 'pessoa' as const, texto: i.pergunta }, { papel: 'ia' as const, texto: i.resposta.texto }] : [],
    )
    const id = Date.now()
    setItens((atual) => [...atual, { id, pergunta: p }])
    setTexto('')
    setEnviando(true)
    try {
      const resposta = await perguntar(p, historico, marcaId, periodoTela)
      setItens((atual) => atual.map((i) => (i.id === id ? { ...i, resposta } : i)))
    } catch (e) {
      setItens((atual) => atual.map((i) => (i.id === id ? { ...i, erro: (e as Error).message } : i)))
    } finally {
      setEnviando(false)
    }
  }

  const de = marca ? ` da ${marca}` : ''
  const exemplos = periodoTela
    ? [`Por que o custo por conversão${de} mudou neste período?`, `Quais campanhas${de} merecem atenção neste período?`, 'Compare as marcas neste período.']
    : [
        `Como foi o tráfego pago${de || ' das minhas marcas'} na semana passada?`,
        `Quais campanhas${de} estão com custo por conversão acima da média este mês?`,
        'Compare o investimento e as conversões das marcas nos últimos 30 dias.',
        `O que mudou no orgânico${de} nos últimos 30 dias?`,
      ]
  const largura = compacto ? '100%' : '85%'

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      {!itens.length ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {exemplos.map((e) => (
            <Chip key={e} label={e} variant="outlined" onClick={() => enviar(e)} sx={{ height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
          ))}
        </Stack>
      ) : null}

      <Stack spacing={2}>
        {itens.map((i) => (
          <Stack key={i.id} spacing={1}>
            <Paper sx={{ alignSelf: 'flex-end', maxWidth: largura, px: 2, py: 1.25, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {i.pergunta}
              </Typography>
            </Paper>
            {i.resposta ? (
              <Resposta r={i.resposta} onPerguntar={enviar} desabilitado={enviando} largura={compacto ? '100%' : '92%'} />
            ) : i.erro ? (
              <Alert severity="error">{i.erro}</Alert>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, maxWidth: largura }}>
                <Typography variant="caption" color="text.secondary">
                  Buscando os dados e escrevendo a resposta…
                </Typography>
                <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
              </Paper>
            )}
          </Stack>
        ))}
        <div ref={fim} />
      </Stack>

      <Paper
        variant="outlined"
        component="form"
        onSubmit={(e) => {
          e.preventDefault()
          enviar(texto)
        }}
        sx={{ p: 1.5, position: 'sticky', bottom: compacto ? 0 : 12, display: 'flex', gap: 1, alignItems: 'flex-end', bgcolor: 'background.paper' }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={6}
          size="small"
          placeholder="Pergunte sobre investimento, campanhas, busca orgânica, redes sociais…"
          value={texto}
          onChange={(e) => setTexto(e.target.value.slice(0, 1000))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              enviar(texto)
            }
          }}
          slotProps={{ htmlInput: { 'aria-label': 'Sua pergunta' } }}
        />
        <Button type="submit" variant="contained" disabled={enviando || !texto.trim()} sx={{ textTransform: 'none' }}>
          Perguntar
        </Button>
      </Paper>
    </Stack>
  )
}

function Resposta({ r, onPerguntar, desabilitado, largura }: { r: RespostaChat; onPerguntar: (p: string) => void; desabilitado: boolean; largura: string }) {
  const [verFontes, setVerFontes] = useState(r.tipo === 'fontes')
  return (
    <Paper variant="outlined" sx={{ p: 2, maxWidth: largura }}>
      {r.tipo === 'fontes' ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {r.texto}
        </Alert>
      ) : (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
          {r.texto}
        </Typography>
      )}

      {r.avisos.length ? (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          Não consultado: {r.avisos.join('; ')}.
        </Typography>
      ) : null}

      {r.fontes.length ? (
        <>
          <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            {r.tipo === 'ia' ? <Chip size="small" variant="outlined" color="primary" label="Números conferidos" /> : null}
            <Button size="small" onClick={() => setVerFontes((v) => !v)} sx={{ textTransform: 'none', px: 0.5 }}>
              {verFontes ? 'Esconder os dados usados' : `Ver os dados usados (${r.fontes.length} ${r.fontes.length === 1 ? 'consulta' : 'consultas'})`}
            </Button>
          </Stack>
          <Collapse in={verFontes}>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {r.fontes.map((f) => (
                <Box key={f.titulo}>
                  <Typography variant="subtitle2">{f.titulo}</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                    {f.achados.map((a) => (
                      <Typography component="li" variant="body2" color="text.secondary" key={a}>
                        {a}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Collapse>
        </>
      ) : null}

      {r.sugestoes.length ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
          {r.sugestoes.map((s) => (
            <Chip key={s} size="small" label={s} onClick={() => onPerguntar(s)} disabled={desabilitado} />
          ))}
        </Stack>
      ) : null}
    </Paper>
  )
}
